// reports/ReportTable.tsx
import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Download } from "lucide-react";
import type { ReportColumn, ReportFilters, ReportRow, ShipmentMode } from "./Types";
import { getColumns } from "./Reportconfig";
import { exportToCsv, exportToExcel, getCellValue } from "./Reportutils";
import { CustomLoadingOverlay, CustomNoRowsOverlay } from "../../common/Datagridoverlays";
import { FOOTER_HEIGHT, HEADER_HEIGHT, MAX_GRID_HEIGHT, MIN_GRID_HEIGHT, ROW_ESTIMATE } from "../../components/Contants/constantValue";


/* --- CD Track brand accent (keep in sync with ReportFilterBar) --- */
const ACCENT_SOFT = "rgba(245, 165, 36, 0.12)";
const ACCENT_STRIPE = "rgba(245, 165, 36, 0.06)";


interface Props {
  mode: ShipmentMode;
  rows: ReportRow[];
  hasGenerated: boolean;
  loading: boolean;
  filters: ReportFilters;
}

/* Keeps the icon + label on one line regardless of the global .btn flex direction */
const btnContentStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
};

const ReportTable: React.FC<Props> = ({ mode, rows, hasGenerated, loading, filters }) => {
  const columnDefs: ReportColumn[] = getColumns(mode);

  const gridColumns: GridColDef<ReportRow>[] = useMemo(
    () =>
      columnDefs.map((col) => ({
        field: col.field,
        headerName: col.headerName,
        width: col.width ?? 140,
        sortable: col.kind !== "flags",
        // v7 signature: (value, row) — display value == exported value
        valueGetter: (_value: unknown, row: ReportRow) => getCellValue(row, col, mode),
      })),
    [columnDefs, mode]
  );

  // First load / before any Generate: show a prompt, not a grid.
  if (!hasGenerated) {
    return (
      <div className="whiteBox">
        <div className="subscriptioncell">
          <Box
            sx={{
              p: 6,
              textAlign: "center",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">
              Select filters and generate a report to see {mode === "air" ? "air" : "ocean"} shipments here.
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  const hasRows = rows.length > 0;

  const gridHeight = Math.min(
    MAX_GRID_HEIGHT,
    Math.max(
      MIN_GRID_HEIGHT,
      HEADER_HEIGHT + FOOTER_HEIGHT + Math.max(rows.length, 1) * ROW_ESTIMATE
    )
  );

  return (
    <div className="whiteBox">
      <div className="subscriptioncell">
        {/* Records count + download actions on one line, outside the grid */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {rows.length} matching record{rows.length === 1 ? "" : "s"}
          </Typography>
          <Stack direction="row" gap={1}>
            <button
              className="btn btnYellow"
              disabled={!hasRows}
              onClick={() => exportToExcel(rows, columnDefs, mode, filters)}
            >
              <span style={btnContentStyle}>
                <Download className="w-3.5 h-3.5" />
                Download Excel
              </span>
            </button>
            <button
              className="btn btnYellow"
              disabled={!hasRows}
              onClick={() => exportToCsv(rows, columnDefs, mode, filters)}
            >
              <span style={btnContentStyle}>
                <Download className="w-3.5 h-3.5" />
                Download CSV
              </span>
            </button>
          </Stack>
        </div>

        <Box
          sx={{
            height: gridHeight,
            width: "100%",
            boxShadow: "0 1px 2px rgba(0, 0, 0, .06), 0 2px 8px rgba(0, 0, 0, .05)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={gridColumns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            slotProps={{
              loadingOverlay: { message: "Loading report..." },
              noRowsOverlay: { message: "No results found for these filters" },
            }}
            sx={{
              // Keeps overlays from collapsing to a sliver when there are no rows
              "--DataGrid-overlayHeight": "300px",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
              // Header: gold, bold, title-case labels + strong bottom divider
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "background.paper",
                borderBottom: "2px solid",
                borderColor: ACCENT_SOFT,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.9rem",
                textTransform: "none",
              },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              // Cells: vertically centered (needed for auto row height) + light divider
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "rgba(0,0,0,0.06)",
                fontSize: "0.875rem",
                color: "text.primary",
              },
              // Zebra striping (cream) + amber hover, matching the contacts table
              "& .MuiDataGrid-row": {
                opacity: "1 !important",
                "&:nth-of-type(even)": { bgcolor: ACCENT_STRIPE },
                "&:hover": { bgcolor: ACCENT_SOFT },
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid",
                borderColor: "divider",
              },
            }}
            rowHeight={ROW_ESTIMATE}
            columnHeaderHeight={HEADER_HEIGHT}
          />
        </Box>
      </div>
    </div>
  );
};

export default ReportTable;